/**
 * =============================================================================
 * Registry central de erros de autenticação — Extranet KB
 * =============================================================================
 *
 * Mapeia códigos de erro do Supabase Auth e do backend (edge kb-extranet-login /
 * kb-extranet-signup) para mensagens PT-BR amigáveis, prontas para renderizar
 * como um alerta inline no formulário.
 *
 * Regras:
 *  - Nunca ecoar a mensagem original do Supabase (evita vazar detalhes internos
 *    e evita mostrar textos em inglês para o usuário final).
 *  - Anti-enumeração: erros que revelariam existência de conta (`user_not_found`)
 *    são normalizados para `invalid_credentials`.
 *  - Fallback estável: qualquer código desconhecido cai em uma mensagem
 *    genérica + um `correlation_id` curto para o usuário citar ao suporte.
 *
 * Uso:
 *   const info = resolveAuthError({ code: e.code, status: e.status, message: e.message });
 *   setAlert(info);
 */

import { logger } from '@/lib/logger';

// -----------------------------------------------------------------------------
// Tipos públicos
// -----------------------------------------------------------------------------
export type AuthErrorSeverity = 'error' | 'warning' | 'info';

export interface AuthErrorAction {
  label: string;
  /** Caminho relativo — a UI concatena com o tenantSlug quando aplicável. */
  href?: string;
  /** Marca ações que devem redirecionar via navigate imediatamente. */
  redirect?: boolean;
}

export interface AuthErrorInfo {
  /** Código canônico já normalizado (ex: `invalid_credentials`). */
  code: string;
  title: string;
  description: string;
  severity: AuthErrorSeverity;
  action?: AuthErrorAction;
  /** ID curto exibido no rodapé do alerta para tracking com suporte. */
  correlationId: string;
}

export interface AuthErrorInput {
  /** Código estruturado retornado pela edge function (prioridade máxima). */
  backendCode?: string | null;
  /** `error.code` do @supabase/supabase-js (AuthApiError). */
  code?: string | null;
  /** HTTP status (útil para 429 quando não vem `code`). */
  status?: number | null;
  /** Mensagem original (só usada como último recurso via regex). */
  message?: string | null;
}

// -----------------------------------------------------------------------------
// Helper: gera um correlation id curto para o usuário informar ao suporte
// -----------------------------------------------------------------------------
function generateCorrelationId(): string {
  try {
    const uuid = crypto.randomUUID();
    return uuid.replace(/-/g, '').slice(0, 8).toUpperCase();
  } catch {
    return Math.random().toString(36).slice(2, 10).toUpperCase();
  }
}

// -----------------------------------------------------------------------------
// Normalização de código
// -----------------------------------------------------------------------------
/**
 * Prioridade: backendCode > code (Supabase) > status(429) > heurística de
 * mensagem. Sempre retorna algo — nunca `null`.
 */
function normalizeCode(input: AuthErrorInput): string {
  if (input.backendCode) return input.backendCode;
  if (input.code) {
    // Anti-enumeração: não distinguir user_not_found de invalid_credentials
    if (input.code === 'user_not_found') return 'invalid_credentials';
    return input.code;
  }
  if (input.status === 429) return 'over_request_rate_limit';

  const msg = (input.message ?? '').toLowerCase();
  if (!msg) return 'unknown';

  if (msg.includes('failed to fetch') || msg.includes('networkerror')) return 'network_error';
  if (msg.includes('invalid login credentials') || msg.includes('invalid credentials')) {
    return 'invalid_credentials';
  }
  if (msg.includes('email not confirmed')) return 'email_not_confirmed';
  if (msg.includes('already registered') || msg.includes('user already registered')) {
    return 'email_taken';
  }
  if (msg.includes('rate limit') || msg.includes('too many')) return 'over_request_rate_limit';
  if (msg.includes('weak password') || msg.includes('password should')) return 'weak_password';
  if (msg.includes('same password')) return 'same_password';
  if (msg.includes('session missing') || msg.includes('session expired')) return 'session_expired';
  if (msg.includes('tenant not found') || msg.includes('organização não encontrada')) {
    return 'tenant_not_found';
  }
  return 'unknown';
}

// -----------------------------------------------------------------------------
// Mapa canônico
// -----------------------------------------------------------------------------
type MapEntry = Omit<AuthErrorInfo, 'correlationId' | 'code'>;

const REGISTRY: Record<string, MapEntry> = {
  // --- Credenciais / login ---------------------------------------------------
  invalid_credentials: {
    title: 'E-mail ou senha incorretos',
    description: 'Verifique os dados informados e tente novamente.',
    severity: 'error',
    action: { label: 'Esqueci a senha', href: '/esqueci-senha' },
  },
  email_not_confirmed: {
    title: 'Confirme seu e-mail antes de entrar',
    description:
      'Enviamos um link de confirmação para o e-mail cadastrado. Verifique também a pasta de spam.',
    severity: 'warning',
    action: { label: 'Reenviar confirmação', href: '/confirme-email' },
  },

  // --- Rate limit ------------------------------------------------------------
  over_request_rate_limit: {
    title: 'Muitas tentativas em pouco tempo',
    description: 'Aguarde alguns minutos e tente novamente.',
    severity: 'warning',
  },
  over_email_send_rate_limit: {
    title: 'Limite de envio de e-mails atingido',
    description: 'Aguarde alguns minutos antes de solicitar um novo e-mail.',
    severity: 'warning',
  },

  // --- Cadastro / senha ------------------------------------------------------
  email_taken: {
    title: 'Este e-mail já está cadastrado',
    description: 'Faça login ou use a opção "Esqueci a senha" para recuperar o acesso.',
    severity: 'warning',
    action: { label: 'Ir para o login', href: '/login' },
  },
  weak_password: {
    title: 'Senha muito fraca',
    description: 'Use ao menos 8 caracteres combinando letras, números e um símbolo.',
    severity: 'error',
  },
  same_password: {
    title: 'A nova senha não pode ser igual à atual',
    description: 'Escolha uma senha diferente da anterior.',
    severity: 'error',
  },
  signup_disabled: {
    title: 'Cadastros temporariamente desativados',
    description: 'Fale com o administrador para receber um convite.',
    severity: 'info',
  },

  // --- Sessão / recovery -----------------------------------------------------
  session_expired: {
    title: 'Sessão expirada',
    description: 'Solicite um novo link para redefinir a senha.',
    severity: 'error',
    action: { label: 'Solicitar novo link', href: '/esqueci-senha' },
  },

  // --- Backend (edge kb-extranet-login) --------------------------------------
  no_external_user: {
    title: 'Este e-mail não tem acesso a esta organização',
    description:
      'Confirme se você está no portal correto ou solicite acesso ao administrador.',
    severity: 'error',
  },
  inactive: {
    title: 'Acesso aguardando aprovação',
    description: 'Um administrador precisa aprovar seu cadastro antes do primeiro login.',
    severity: 'info',
  },
  pending_signup: {
    title: 'Acesso aguardando aprovação',
    description: 'Um administrador precisa aprovar seu cadastro antes do primeiro login.',
    severity: 'info',
  },
  suspended: {
    title: 'Seu acesso foi suspenso',
    description: 'Fale com o administrador da organização para reativar.',
    severity: 'error',
  },
  tenant_not_found: {
    title: 'Organização não encontrada',
    description: 'Verifique o endereço do portal ou fale com o administrador.',
    severity: 'error',
  },

  // --- Rede ------------------------------------------------------------------
  network_error: {
    title: 'Sem conexão',
    description: 'Verifique sua internet e tente novamente.',
    severity: 'error',
  },

  // --- Fallback --------------------------------------------------------------
  unknown: {
    title: 'Não foi possível concluir a operação',
    description: 'Tente novamente em instantes. Se o problema persistir, fale com o suporte.',
    severity: 'error',
  },
};

// -----------------------------------------------------------------------------
// Função principal
// -----------------------------------------------------------------------------
/**
 * Resolve um erro de autenticação da extranet em um objeto pronto para render.
 * Sempre loga o input bruto via `logger.warn` com o mesmo correlationId
 * mostrado ao usuário, facilitando o rastreio.
 */
export function resolveAuthError(input: AuthErrorInput): AuthErrorInfo {
  const correlationId = generateCorrelationId();
  const code = normalizeCode(input);
  const entry = REGISTRY[code] ?? REGISTRY.unknown;

  logger.warn('extranet-auth', 'resolveAuthError', {
    correlationId,
    resolvedCode: code,
    input,
  });

  return {
    code,
    correlationId,
    title: entry.title,
    description: entry.description,
    severity: entry.severity,
    action: entry.action,
  };
}
